import {getUserAccountProfileExistStatus} from '@/api/api';
import {isDevelopmentEnvironment} from '@/common/isTestEnvironment';
import {ArrowButton} from '@/components/Buttons/ArrowButton';
import {InternationalPhoneInput} from '@/components/InternationalPhoneInput/InternationalPhoneInput';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

export const MLSAuthSignin = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const isValidPhoneNumber = async (phoneNumber: string) => {
    // Skip validation in development environment to allow for firebase test numbers
    if (isDevelopmentEnvironment) {
      return true;
    }
    try {
      // We use dynamic import to avoid bundling google-libphonenumber in the main bundle.
      const PhoneNumberUtil = await import('google-libphonenumber').then((module) => module.PhoneNumberUtil);
      const phoneUtil = PhoneNumberUtil.getInstance();
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phoneNumber));
    } catch (error) {
      return false;
    }
  };

  const handleCheckPhoneNumber = async (phoneNumber: string) => {
    setIsLoading(true);
    setError('');
    try {
      if (!phoneNumber) {
        setError('Phone number is required');
        return;
      }
      if (!(await isValidPhoneNumber(phoneNumber))) {
        setError('Invalid phone number');
        return;
      }
      const formattedPhone = phoneNumber.replace(/[\s()-]/g, '');
      const response = await getUserAccountProfileExistStatus({
        phoneNumber: formattedPhone,
      });
      if (response.data.accountExists) {
        navigate('/auth/signin/phone', {state: {phoneNumber: formattedPhone}});
      } else {
        setError('Account with this phone number does not exist');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-black">
      <div className="flex relative flex-col min-h-screen max-w-lg mx-auto flex-1 text-white items-center justify-start px-4">
        <ArrowButton
          className="absolute top-4 left-2"
          onClick={() => navigate('/auth')}
          variant="secondary"
          arrowDirection="left"
        />

        <div className="mt-36 md:mt-48 flex flex-col items-center justify-center text-center">
          <h1 className="text-4xl md:text-5xl font-teko font-bold mb-2">WHAT IS YOUR PHONE NUMBER?</h1>
          <p data-test-id="phone-login-text" className="font-helvetica font-thin text-base md:text-lg mb-8">
            Enter your phone number to confirm and verify your account
          </p>

          <div className="w-full relative max-w-md flex flex-col">
            <InternationalPhoneInput
              value={phoneNumber}
              onChange={(phone) => {
                setPhoneNumber(phone);
                setError('');
              }}
              disabled={isLoading}
              className="mt-4"
            />
            {error && (
              <p data-test-id="phone-login-error-message" className="text-mls-error text-left text-sm mt-1">
                {error}
              </p>
            )}

            <ArrowButton
              data-test-id="signin-with-phone-number-submit-button"
              className="mt-12 self-end"
              onClick={() => handleCheckPhoneNumber(phoneNumber)}
              hasError={!!error}
              loading={isLoading}
            />
          </div>

          <div className="w-full fixed bottom-8 left-0 md:left-auto px-6 max-w-md">
            <div className="w-full flex items-center gap-4 mb-6">
              <div className="h-px flex-1 bg-white opacity-70" />
              <span className="text-gray-400 text-base">or</span>
              <div className="h-px flex-1 bg-white opacity-70" />
            </div>

            <button
              data-test-id="signin-with-email-button"
              onClick={() => navigate('/auth/signin/email')}
              className="p-2 text-white text-base border border-white rounded-full w-full"
            >
              Sign in with email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
